import { IconButton, type IconButtonProps } from '@radix-ui/themes';
import { ChevronLeftIcon, ChevronRightIcon, ChevronsLeftIcon, ChevronsRightIcon, type LucideProps } from 'lucide-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type PaginationProps = {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  maxAroundCurrent?: number;
  variant?: IconButtonProps['variant'];
  color?: IconButtonProps['color'];
  highContrast?: IconButtonProps['highContrast'];
  iconSize?: LucideProps['size'];
};

export const Pagination = ({
  totalPages,
  currentPage,
  onPageChange,
  maxAroundCurrent = 4,
  variant = 'outline',
  color = 'gray',
  iconSize = 18,
  highContrast = true,
}: PaginationProps) => {
  const { t } = useTranslation();
  // Calculate if we should pick one extra page, to avoid [1, '...', 3]/[8, '...', 10]
  const oneBetweenStart = currentPage - (1 + maxAroundCurrent + 1) === 1;
  const oneBetweenEnd = totalPages - (currentPage + maxAroundCurrent + 1) === 1;

  const getPageNumbers = useCallback(() => {
    const pages = [];

    // Always show first page
    pages.push(1);

    // Show ellipsis if current page is not near the start
    if (currentPage - maxAroundCurrent > 3) {
      pages.push('...');
    }

    // Show pages around current page
    const start = Math.max(2, currentPage - (oneBetweenStart ? maxAroundCurrent + 1 : maxAroundCurrent));
    const end = Math.min(totalPages - 1, currentPage + (oneBetweenEnd ? maxAroundCurrent + 1 : maxAroundCurrent));
    for (let i = start; i <= end; i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }

    // Show ellipsis if current page is not near the end
    if (currentPage + maxAroundCurrent < totalPages - 2) {
      pages.push('...');
    }

    // Always show last page
    if (!pages.includes(totalPages)) {
      pages.push(totalPages);
    }

    return pages;
  }, [totalPages, currentPage, maxAroundCurrent, oneBetweenStart, oneBetweenEnd]);

  const pageNumbers = getPageNumbers();

  return (
    <>
      <IconButton
        aria-label={t('pagination.firstPage')}
        color={color}
        highContrast={highContrast}
        variant={variant}
        onClick={() => onPageChange(1)}
        disabled={currentPage <= 1}
      >
        <ChevronsLeftIcon size={iconSize} />
      </IconButton>

      <IconButton
        aria-label={t('pagination.previousPage')}
        color={color}
        highContrast={highContrast}
        variant={variant}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
      >
        <ChevronLeftIcon size={iconSize} />
      </IconButton>

      {pageNumbers.map((page, index) => (
        <IconButton
          aria-label={typeof page === 'number' ? t('pagination.pageN', { page }) : t('pagination.ellipsis')}
          key={index}
          color={color}
          highContrast={highContrast}
          variant={currentPage === page ? 'solid' : variant}
          onClick={() => typeof page === 'number' && onPageChange(page)}
        >
          {page}
        </IconButton>
      ))}

      <IconButton
        aria-label={t('pagination.nextPage')}
        color={color}
        highContrast={highContrast}
        variant={variant}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
      >
        <ChevronRightIcon size={iconSize} />
      </IconButton>

      <IconButton
        aria-label={t('pagination.lastPage')}
        color={color}
        highContrast={highContrast}
        variant={variant}
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage >= totalPages}
      >
        <ChevronsRightIcon size={iconSize} />
      </IconButton>
    </>
  );
};
